import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import Head from 'next/head'
import Script from 'next/script'
import { ErrorBoundary, ErrorFallback } from '@/layout/ErrorBoundary'
import { SiteNavContextProvider } from '@/organisms/SiteNav/SiteNavContext'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { BaseLayout } from '../BaseLayout'
import { TicketNavBar } from './components/TicketNavBar'
import { TicketNavVariantTypes } from './components/TicketNavBar/TicketNav'

interface DefaultLayoutProps {
  children: React.ReactNode
  title?: string
  regions?: TheatricalReleaseRegion[]
  shouldRemoveNavOnMobile?: boolean
  isDarkMode?: boolean
  className?: string
  navClassName?: string
  shouldShowBackNavigation?: boolean
  shouldShowCloseNavigation?: boolean
  onNavigationClose?: () => void
  shouldShowLanguageModal?: boolean
  useDefaultNav?: boolean
  isLoggedIn?: boolean
}

export const TicketsLayout: React.FC<DefaultLayoutProps> = ({
  children,
  regions,
  shouldRemoveNavOnMobile = false,
  className,
  title,
  isDarkMode = true,
  navClassName,
  shouldShowBackNavigation = false,
  shouldShowCloseNavigation = false,
  shouldShowLanguageModal = false,
  onNavigationClose,
  useDefaultNav = false,
  isLoggedIn,
}) => {
  const { query, isReady, back, asPath, push } = useSafeRouter()
  const shouldShowBack = shouldShowBackNavigation || (isReady && query?.navigation === 'back')

  const navigationVariant = useMemo<TicketNavVariantTypes>(() => {
    if (shouldShowBack) return 'BACK'
    if (shouldShowCloseNavigation) return 'CLOSE'
    return 'NONE'
  }, [shouldShowBack, shouldShowCloseNavigation])

  const goBack = useCallback(() => {
    if (asPath?.includes('seats')) {
      if (query?.reservationId) return back()
      const basePath = asPath?.split('showtime')[0]
      if (basePath) return push(basePath)
    }
    back()
  }, [asPath, back, push, query?.reservationId])

  const handleNavigationIconClick = useCallback(() => {
    if (shouldShowBack) return goBack()
    if (shouldShowCloseNavigation && onNavigationClose) return onNavigationClose()
    return null
  }, [goBack, onNavigationClose, shouldShowBack, shouldShowCloseNavigation])

  return (
    <>
      <Head>
        <link rel="dns-prefetch" href="//app.omniconvert.com" />
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `window._mktz=window._mktz || [];`,
          }}
        />
      </Head>
      <Script src="//cdn.omniconvert.com/js/v33131c.js" />

      <BaseLayout isDarkMode={isDarkMode}>
        <div className={classNames(isDarkMode ? 'bg-core-gray-950' : 'bg-core-gray-100', className)}>
          <SiteNavContextProvider
            gradientBackground={false}
            hideNavigation={false}
            hideMobileNavigation={false}
            regions={regions}
            transparentBackground={false}
            variant={'absolute'}
            renderHeaderWrapper={false}
          >
            <TicketNavBar
              shouldShowLanguageModal={shouldShowLanguageModal}
              regions={regions}
              useDefaultNav={useDefaultNav}
              shouldHideOnMobile={shouldRemoveNavOnMobile}
              navClassName={navClassName}
              title={title}
              navigationVariant={navigationVariant}
              onNavigationIconClick={handleNavigationIconClick}
              isLoggedIn={isLoggedIn}
            />
          </SiteNavContextProvider>
          <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>
        </div>
      </BaseLayout>
    </>
  )
}
